/**
 * The page frame — the deck's `.page`, established ONCE.
 *
 * `mockups/tokens.css:295-296` is the layout specification, and it is two rules, not one:
 *
 * ```css
 *   .content { flex: 1; min-width: 0; overflow-y: auto; }   ← the SCROLLER  (our <main>)
 *   .page    { padding: 24px 28px 64px; }                   ← the PAGE      (this component)
 * ```
 *
 * ⛔ THEY ARE DELIBERATELY DIFFERENT ELEMENTS, and collapsing them onto `<main>` is the mistake
 * this docblock exists to prevent. `<main>` must stay full-bleed: it is the scroll container, so a
 * width constraint on it would strand the scrollbar in the middle of a wide window, with content on
 * only one side of it. `SectionLayout.tsx` owns the first line; this file owns the second.
 *
 * ⛔ NO HORIZONTAL AUTO-MARGIN. `.page` is a block child of `.content`, so the deck's page is
 * LEFT-ALIGNED — flush with the rail. That is a design decision and not an oversight: the rail is
 * the left edge of the interface, and centring the page would open a growing gap between the
 * navigation and the content it navigates, so every rail click would move the eye further right the
 * wider the display. `pane-frame.test.tsx` asserts the absence of `mx-auto` for exactly this reason.
 *
 * ## ⭐⭐ THE RULE THIS FILE NOW ENCODES: **CAP THE MEASURE OF PROSE, NEVER THE WIDTH OF DATA.**
 *
 * The frame carried `max-w-[1400px]` until 2026-08-06, justified in this very docblock as *"the
 * deck's number"*. ⛔ **It was a MOCKUP'S CANVAS being enforced as a PRODUCT CONSTRAINT, and the two
 * are not the same claim.** A deck is authored at one artboard size; `1400px` records how wide that
 * artboard was, not how wide a table should be. Nothing measured it against a real display.
 *
 * What it cost, from the owner on a 2000px+ monitor: *"We aren't using the full width of large
 * monitors well at all!!! What gives?"* The arithmetic is unarguable — the rail is `w-60` (240px),
 * so at 2000px the content region is 1760px and the frame used 1400 of it. **360px, a fifth of the
 * usable width, rendered as empty page**, on every screen, permanently. At 2560px it is 920px, more
 * than a third.
 *
 * ⇒ **There is no width cap here any more.** Tables, grids, charts and every other data region now
 * fill the viewport, which is the only thing a wide monitor is FOR: more rows and more columns
 * visible at once, which is fewer scrolls and fewer horizontal truncations.
 *
 * ⛔ **This is NOT licence to set a paragraph 200 characters wide, and the distinction is the whole
 * point of the rule.** Line length and layout width are different problems with different limits:
 *
 * | | what bounds it | why |
 * |---|---|---|
 * | **prose** | a `ch` MEASURE on the paragraph | past ~100 characters the eye loses its place on the return sweep — a reading limit, independent of the display |
 * | **data** | nothing; fill the viewport | a table has no return sweep. Width buys columns |
 *
 * ⚠️ **The prose half is already enforced, and by a better mechanism than this cap ever was.**
 * `panes/__tests__/prose-measure.test.ts` fails the build on any `<p>` of ≥70 literal characters in
 * a pane that does not declare a `max-w` — 81 `max-w-[100ch]` declarations satisfy it today — and
 * `PageHeading`'s `PAGE_SUB_CLASS` carries the deck's own narrower `max-w-[78ch]`. Both are in `ch`,
 * so they are font-relative and stay correct at any type scale. **A page-level pixel cap could never
 * have done that job**: it bounded the paragraph and the table by the same number, which is exactly
 * one number too few — and at 1400px it still permitted a ~200-character line, so it was not even
 * succeeding at the job it was mistakenly credited with.
 *
 * ⇒ ⛔ **Do not "restore" a `max-w-*` here** because a screen looks sparse on a wide display. A
 * sparse screen is a PANE-LEVEL problem with a pane-level fix: a card grid should gain columns
 * (`xl:grid-cols-4`, `2xl:grid-cols-5`) rather than stretch two columns to 2000px. Capping the page
 * hides that everywhere instead of fixing it anywhere.
 *
 * ## Why this is a component and not a convention
 *
 * It was a convention — `p-8` at the root of each pane — and the convention had already failed in
 * three ways at once when the owner hit it live: fifteen panes said `p-8`, two said `p-6`, and
 * **two said nothing at all** (`FleetCapacityPane`, `InFlightPane`) and shipped their content hard
 * against the viewport edge. Twenty declarations of one rule cannot be kept in agreement by
 * remembering. `AppLoader` renders EVERY routed pane — global, detail and app-scoped — so applying
 * the frame there makes it structural: a pane cannot opt out, and the twenty-first pane inherits it
 * without knowing it exists.
 *
 * ⇒ ⛔ **Panes must NOT declare their own page padding.** The frame supplies it. A pane that also
 * carries `p-8` renders 60px of padding, not 32.
 */
import type { ReactNode } from 'react';

/**
 * The frame's utilities, exported so the contract has ONE definition that a test can read.
 *
 * `px-7` = 28px · `pt-6` = 24px — each the deck's number.
 *
 * A frame that holds a pane scroll region (`[data-pane-region]`) sets both to 0, and the region
 * carries the same padding inside the scroller. So content cuts at the top bar border and the
 * scrollbar sits at the window edge. A screen with no region (the four Billing views, a pane's
 * forbidden state) keeps the frame padding.
 *
 * ⛔ `w-full` and NO `max-w-*`. See the header for why the `max-w-[1400px]` that sat between them is
 * gone and must not come back: it was an artboard size, and it left a fifth of a 2000px display
 * empty. Prose keeps its own `ch` measure per element; data fills the viewport.
 *
 * ⛔⛔ `flex h-full flex-col` — and **`h-full`, NOT `min-h-full`.** This is the height link that lets
 * a pane bound its own list instead of scrolling the page (the owner's biggest item — see
 * `PaneScrollLayout.tsx`), and getting it wrong is what made that whole mechanism inert.
 *
 * ## ⛔ THE `min-h-full` REGRESSION — MEASURED IN A REAL BROWSER, DO NOT RESTORE IT
 *
 * This class said `min-h-full` from the sweep until 2026-08-06, under the reasoning *"`min-h-full`
 * gives a pane a definite height to fill; `h-full` or `overflow-hidden` would CLIP a pane that grows
 * past the frame."* **The second half is true only of the PAIR, and the first half is false.**
 *
 * `min-height` is a FLOOR and never a CEILING. So the frame was free to grow to its content, and it
 * did: `PaneScrollLayout`'s region is `flex-1 min-h-0`, whose max-content contribution is its whole
 * list, so the frame sized to the list, exceeded `<main>`, and **`<main>` (`overflow-y-auto`)
 * scrolled** — carrying the pane's chrome, which lives inside `<main>`, off the top of the screen.
 * ⇒ The region never scrolled at all. Measured in headless Chrome at 1440×900 on 15 routes:
 * **9 scrolled `<main>` instead of the region** (Tenants 627px, Access→Cutover 871px, In-flight
 * 338px, Adoption 204px, Capacity 198px, Versions 156px, Data planes 80px, People 77px, Alerts
 * 41px) with `region.scrollTop` pinned at **0** on every one of them. At 1280×700 it was 11 of 15.
 * With `h-full` all 15 pass: `main.scrollTop` 0, the overflow moves into the region, and the pane
 * chrome's `getBoundingClientRect().top` is invariant under a scroll-to-bottom. Since 2026-10-08
 * the chrome is inside the region and scrolls with it (issue #3888); `main.scrollTop` stays 0.
 *
 * ⛔ **`h-full` ALONE DOES NOT CLIP** — that fear was about `h-full` **+** `overflow-hidden`. This
 * element stays `overflow: visible`, so a pane that grows past the frame still overflows into
 * `<main>` and `<main>` still scrolls it, exactly as before. Nothing is unreachable; the only change
 * is that a pane which HAS bounded its list now actually gets a bounded box to do it in.
 *
 * ⛔ `overflow-hidden` still does not belong here, for the original reason.
 *
 * ## ⛔ THE HISTORY OF THE MISSING `pb-16` — read this before adding it back
 *
 * The deck's `.page` is `padding: 24px 28px 64px`, so this class carried **`pb-16`** (= 64px) until
 * the primitive sweep completed. That 64px was the deck's value and it did real work: it kept the
 * last row of a long table off the bottom edge, where a row sitting flush reads as truncated rather
 * than as the end of the list.
 *
 * ⚠️ **It moved, it was not dropped.** Every pane now bounds its own list with `PaneScrollLayout`,
 * whose scroll region supplies that 64px **inside** the scroller (`SCROLL_REGION_CLASS`'s own
 * `pb-16`) — which is where it has to be. The region has no border and no background, so its bottom
 * edge is invisible: with the padding *outside* the scroller, rows clip mid-height at an invisible
 * line with empty page below, and that reads as breakage rather than as padding. While panes were
 * mid-migration the layout cancelled the frame's padding with a `-mb-16`, documented as the one piece
 * of that file with an expiry date; **both the `pb-16` here and that cancellation were removed
 * together when the last of the 22 panes adopted the layout.**
 *
 * ⛔ **So do not re-add `pb-16` here.** It would restore the double-spacing this pair existed to
 * avoid — 128px of dead space below every list — and there would be nothing left to cancel it.
 * `pane-scroll-layout.test.tsx` asserts both halves of this and explains the invariant.
 */
export const PANE_FRAME_CLASS =
  'flex h-full w-full flex-col px-7 pt-6 has-[[data-pane-region]]:px-0 has-[[data-pane-region]]:pt-0';

export interface PaneFrameProps {
  readonly children: ReactNode;
}

export function PaneFrame({ children }: PaneFrameProps): React.JSX.Element {
  return (
    <div data-testid="pane-frame" className={PANE_FRAME_CLASS}>
      {children}
    </div>
  );
}
